import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { sortByAreaName } from '@/lib/domain/regionSurcharge';
import type {
  ShippingFeePolicyDetail,
  ShippingFeePolicyPage,
  ShippingFeePolicyPayload,
  ShippingFeePolicySummary,
  ShippingFeePolicyListParams,
  ShippingFeePagination,
} from '../types/shippingFee';

/**
 * ID50 배송비조건 관리 API. (be-shipping 계약 — order-service.)
 * 게이트웨이 `/admin/v1/shipping/**` → BE `/api/admin/v1/shipping/**` RewritePath 재사용.
 * v2 envelope { success, data, error, meta }. 목록은 meta.pagination(page 1-base).
 * write 는 Idempotency-Key 수용(lenient). 생성 경로가 OpenAPI 스펙에 미반영이라
 * overseas 정책과 동일하게 adminFetch 로 직접 호출한다.
 */
const BASE = '/admin/v1/shipping/fee-policies';

const DEFAULT_SIZE = 20;

interface PaginationEnvelope {
  page?: number | null;
  size?: number | null;
  totalItems?: number | null;
  totalPages?: number | null;
}

interface ListEnvelope {
  success: boolean;
  data?: { items?: ShippingFeePolicySummary[] | null } | null;
  meta?: { pagination?: PaginationEnvelope | null } | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: ShippingFeePolicyDetail | null;
}

/** 목록 행 정규화 — isDefault 미응답(레거시 행)은 false 로 간주(뱃지 미표시). */
const normalizeSummary = (p: ShippingFeePolicySummary): ShippingFeePolicySummary => ({
  ...p,
  isDefault: p.isDefault ?? false,
});

/**
 * 상세를 정규화한다 — BE 가 nested 배열을 null 로 줄 수도 있어 렌더 전에 배열 보장
 * (`.map` 크래시 방지). overseas 정책 hooks 의 동일 가드. isDefault 미응답은 false.
 */
const normalizeDetail = (p: ShippingFeePolicyDetail): ShippingFeePolicyDetail => ({
  ...p,
  isDefault: p.isDefault ?? false,
  tiers: (p.tiers ?? []).map((t) => ({ ...t })),
  // BE 순서(우편번호 앞자리)는 V62 이후 의미가 없어 저장마다 뒤섞인다 — 지역명순으로 고정한다.
  regionSurcharges: sortByAreaName(p.regionSurcharges ?? [], (r) => r.label).map((r) => ({ ...r })),
});

const normalizePagination = (
  meta: PaginationEnvelope | null | undefined,
  fallbackSize: number,
  itemCount: number,
): ShippingFeePagination => ({
  page: meta?.page ?? 1,
  size: meta?.size ?? fallbackSize,
  totalItems: meta?.totalItems ?? itemCount,
  totalPages: meta?.totalPages ?? 1,
});

/** 목록 쿼리스트링 구성 — page 는 1-base(화면) → 0-base(BE)로 변환. */
const buildListQuery = (params: ShippingFeePolicyListParams): string => {
  const q = new URLSearchParams();
  if (params.supplierScope) q.set('supplierScope', params.supplierScope);
  if (params.keyword && params.keyword.trim()) q.set('keyword', params.keyword.trim());
  const page = params.page ?? 1;
  q.set('page', String(Math.max(0, page - 1)));
  q.set('size', String(params.size ?? DEFAULT_SIZE));
  return q.toString();
};

export const shippingFeePoliciesQueryKey = (params: ShippingFeePolicyListParams) =>
  ['admin', 'shipping-fee-policies', params] as const;

export const shippingFeePolicyQueryKey = (policyId: string) =>
  ['admin', 'shipping-fee-policy', policyId] as const;

const fetchList = async (
  params: ShippingFeePolicyListParams,
): Promise<ShippingFeePolicyPage> => {
  const qs = buildListQuery(params);
  const res = await adminFetch(`${BASE}?${qs}`);
  if (!res.ok) throw new Error(`shipping fee policies list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  const items = (env.data?.items ?? []).map(normalizeSummary);
  return {
    items,
    pagination: normalizePagination(
      env.meta?.pagination,
      params.size ?? DEFAULT_SIZE,
      items.length,
    ),
  };
};

const fetchOne = async (policyId: string): Promise<ShippingFeePolicyDetail> => {
  const res = await adminFetch(`${BASE}/${policyId}`);
  if (!res.ok) throw new Error(`shipping fee policy fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('no data');
  return normalizeDetail(env.data);
};

export const useShippingFeePoliciesQuery = (params: ShippingFeePolicyListParams) =>
  useQuery<ShippingFeePolicyPage>({
    queryKey: shippingFeePoliciesQueryKey(params),
    queryFn: () => fetchList(params),
  });

export const useShippingFeePolicyQuery = (policyId: string | null) =>
  useQuery<ShippingFeePolicyDetail>({
    queryKey: shippingFeePolicyQueryKey(policyId ?? ''),
    queryFn: () => fetchOne(policyId as string),
    enabled: policyId != null,
  });

export const shippingFeeDefaultQueryKey = () =>
  ['admin', 'shipping-fee-policy-default'] as const;

/**
 * 기본 템플릿(ID81, M-8) 조회 — `GET /api/admin/v1/shipping/fee-policies/default`.
 * 상품 등록 화면이 신규 상품 자동선택용으로 사용한다. 기본 템플릿 미지정이면 BE 가
 * data=null(또는 404)을 줄 수 있어 null 을 정상 반환한다(에러로 다루지 않음).
 */
const fetchDefault = async (): Promise<ShippingFeePolicyDetail | null> => {
  const res = await adminFetch(`${BASE}/default`);
  // 기본 템플릿 미지정 = 404 허용(없음을 null 로 표현, 화면은 "기본 미지정" 안내).
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`shipping fee default fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  return env.data ? normalizeDetail(env.data) : null;
};

export const useDefaultShippingFeePolicyQuery = (enabled = true) =>
  useQuery<ShippingFeePolicyDetail | null>({
    queryKey: shippingFeeDefaultQueryKey(),
    queryFn: fetchDefault,
    enabled,
  });

export const useShippingFeePolicyMutation = () => {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'shipping-fee-policies'] });
    // isDefault 변경 시 기본 템플릿이 바뀌므로 default 조회도 무효화(자동선택 동기화).
    void qc.invalidateQueries({ queryKey: shippingFeeDefaultQueryKey() });
  };

  const create = useMutation({
    mutationFn: async (
      payload: ShippingFeePolicyPayload,
    ): Promise<ShippingFeePolicyDetail> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('POST', BASE, payload),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`shipping fee policy create failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return normalizeDetail(env.data);
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({
      policyId,
      payload,
    }: {
      policyId: string;
      payload: ShippingFeePolicyPayload;
    }): Promise<ShippingFeePolicyDetail> => {
      const path = `${BASE}/${policyId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('PUT', path, payload),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`shipping fee policy update failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return normalizeDetail(env.data);
    },
    onSuccess: (_, { policyId }) => {
      void qc.invalidateQueries({ queryKey: shippingFeePolicyQueryKey(policyId) });
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: async (policyId: string): Promise<void> => {
      const path = `${BASE}/${policyId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('DELETE', path, ''),
        },
      });
      if (!res.ok) throw new Error(`shipping fee policy delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
